import React from 'react';
import { FieldNames } from 'app/models/fields/names';
import { Section } from 'app/models/types';
import { OVERDRAFT_PROTECTION_QUESTIONS } from 'app/models/questions/overdraft-protection';
import {
  OVERDRAFT_PROTECTION_APP_BORROWER_DATA,
  OVERDRAFT_PROTECTION_GET_LOAN_AMOUNT_SECTION,
  OVERDRAFT_PROTECTION_CREDIT_REPORT_RESULT_QUESTION,
  OVERDRAFT_PROTECTION_GET_FICO_AND_ZIP_SECTION,
  OVERDRAFT_PROTECTION_APP_FINAL_SECTION,
  OVERDRAFT_PROTECTION_FINAL_QUESTION,
  OVERDRAFT_PROTECTION_APP_CO_BORROWER_DATA,
  OVERDRAFT_PROTECTION_APP_HOUSING,
  OVERDRAFT_PROTECTION_APP_HOMEOWNER,
  OVERDRAFT_PROTECTION_APP_RENTING,
  OVERDRAFT_PROTECTION_APP_RENT_FREE,
  OVERDRAFT_PROTECTION_APP_CO_BORROWER_HOUSING,
  OVERDRAFT_PROTECTION_APP_INCOME_SOURCE,
  OVERDRAFT_PROTECTION_APP_EMPLOYED,
  OVERDRAFT_PROTECTION_APP_RETIRED,
  OVERDRAFT_PROTECTION_APP_EMPLOYEMENT_OTHER,
  OVERDRAFT_PROTECTION_APP_CO_BORROWER_INCOME_SOURCE,
  OVERDRAFT_PROTECTION_APP_CO_BORROWER_EMPLOYED,
  OVERDRAFT_PROTECTION_APP_CO_BORROWER_RETIRED,
  OVERDRAFT_PROTECTION_APP_CO_BORROWER_EMPLOYEMENT_OTHER,
  OVERDRAFT_PROTECTION_APP_CREDIT_AUTHORIZATION,
  OVERDRAFT_PROTECTION_APP_CONGRATS,
  OVERDRAFT_PROTECTION_CHECKING,
} from 'app/models/sections/constants';
import {
  isHomeowner,
  isRentFree,
  isRenting,
  hasCoBorrower,
  isEmployee,
  isSelfEmployed,
  isRetired,
  isEmploymentOther,
  isCoEmployee,
  isCoSelfEmployed,
  isCoBorrowerRetired,
  isCoBorrowerEmploymentOther,
  LoanOfficerCondition,
  noCitizenship,
  coBorrowerHasDifferentAddress,
} from 'app/models/fields/conditionals';
import { AuthorizeCreditReport } from 'app/components/FormAgreements/AuthorizeCreditReport';
import { ajaxValidateBorrowerResidence, ajaxValidateCoBorrowerResidence } from 'app/actions/form/address-validation/actions';
import { ajaxPostCreditReportForOverdraftProtection } from 'app/actions/form/credit-report/actions';
import { Questions } from '../questions/Questions';
import {
  handleOverdraftProtectionFetchLimits,
  handleOverdraftProtectionFetchRates,
} from '../../actions/form/overdraft-protection/actions';

export const getOverdraftProtectionById = (id: number): Section => {
  return OVERDRAFT_PROTECTION_SECTIONS.find((section) => section.id === id) as Section;
};

const overdraftProtectionQuestions = new Questions(OVERDRAFT_PROTECTION_QUESTIONS);

const OVERDRAFT_PROTECTION_SECTIONS: Section[] = [
  {
    id              : OVERDRAFT_PROTECTION_GET_FICO_AND_ZIP_SECTION,
    hideBackButton  : true,
    hideProgressBar : true,
    title           : 'What are the rates and payments?',
    paragraph       : <span>Answer these short questions and we'll provide you with options for a Overdraft Protection Loan.</span>,
    questions       : overdraftProtectionQuestions.getByName([
      FieldNames.creditScore,
      FieldNames.propertyZip,
    ]),
  },
  {
    id              : OVERDRAFT_PROTECTION_CHECKING,
    hideProgressBar : true,
    questions       : overdraftProtectionQuestions.getByName([
      FieldNames.hasCurrentCheckingAccount,
    ]),
  },
  {
    id        : OVERDRAFT_PROTECTION_APP_BORROWER_DATA,
    title     : 'Enter your personal information',
    questions : overdraftProtectionQuestions.getByName([
      FieldNames.firstName,
      FieldNames.middleName,
      FieldNames.lastName,
      FieldNames.suffixName,
      FieldNames.phone,
      FieldNames.email,
      FieldNames.dateOfBirth,
    ]),
    submitAction : handleOverdraftProtectionFetchLimits,
    submitText   : 'Next',
    conditionalIf : {
      referToLoIf : [
        LoanOfficerCondition.hasLowSelfReportedFico,
        LoanOfficerCondition.hasNoCurrentCheckingAccount,
        LoanOfficerCondition.isBorrowerUnderage,
      ],
    },
  },
  {
    id              : OVERDRAFT_PROTECTION_GET_LOAN_AMOUNT_SECTION,
    hideProgressBar : true,
    title           : 'What are the rates and payments?',
    paragraph       : <span>Answer these short questions and we'll provide you with options for a personal loan.</span>,
    questions       : overdraftProtectionQuestions.getByName([
      FieldNames.loanAmount,
    ]),
    submitAction : handleOverdraftProtectionFetchRates,
    submitText   : 'Submit',
  },
  {
    id        : OVERDRAFT_PROTECTION_APP_CO_BORROWER_DATA,
    questions : overdraftProtectionQuestions.getByName([
      FieldNames.coBorrowerYN,
      FieldNames.coBorrowerFirstName,
      FieldNames.coBorrowerMiddleName,
      FieldNames.coBorrowerLastName,
      FieldNames.coBorrowerSuffixName,
      FieldNames.coBorrowerEmail,
      FieldNames.coBorrowerDOB,
    ]),
    conditionalIf : {
      referToLoIf : [LoanOfficerCondition.isCoBorrowerUnderage],
    },
  },
  {
    id        : OVERDRAFT_PROTECTION_APP_HOUSING,
    title     : 'What is your current housing situation?',
    questions : overdraftProtectionQuestions.getByName([FieldNames.livingRentOrOwn]),
  },
  {
    id        : OVERDRAFT_PROTECTION_APP_HOMEOWNER,
    showIf    : [isHomeowner],
    title     : 'Provide your homeowner information',
    questions : overdraftProtectionQuestions.getByName([
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
      FieldNames.livingOccupancyStart,
      FieldNames.livingYearTaxes,
      FieldNames.livingYearInsure,
      FieldNames.livingYearAssociate,
    ]),
    submitAction : ajaxValidateBorrowerResidence,
  },
  {
    id         : OVERDRAFT_PROTECTION_APP_RENTING,
    showIf     : [isRenting],
    title      : 'Provide your renter information',
    questions  : overdraftProtectionQuestions.getByName([
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
      FieldNames.livingMonthlyRentAmount,
      FieldNames.livingOccupancyStart,
    ]),
    submitAction : ajaxValidateBorrowerResidence,
  },
  {
    id         : OVERDRAFT_PROTECTION_APP_RENT_FREE,
    showIf     : [isRentFree],
    title      : 'Provide your housing information',
    questions  : overdraftProtectionQuestions.getByName([
      FieldNames.livingPropertyStreet,
      FieldNames.livingPropertyStreet2,
      FieldNames.livingPropertyCity,
      FieldNames.livingPropertyState,
      FieldNames.livingPropertyZip,
      FieldNames.livingOccupancyStart,
    ]),
    submitAction : ajaxValidateBorrowerResidence,
  },
  {
    id        : OVERDRAFT_PROTECTION_APP_CO_BORROWER_HOUSING,
    showIf    : [hasCoBorrower],
    title     : 'Co-borrower at same address as borrower?',
    questions : overdraftProtectionQuestions.getByName([
      FieldNames.coBorrowerSameAddress,
      FieldNames.coBorrowerLivingPropertyStreet,
      FieldNames.coBorrowerLivingPropertyStreet2,
      FieldNames.coBorrowerLivingPropertyCity,
      FieldNames.coBorrowerLivingPropertyState,
      FieldNames.coBorrowerLivingPropertyZip,
    ]),
    submitAction : (formValues) =>
      coBorrowerHasDifferentAddress(formValues) ? ajaxValidateCoBorrowerResidence(formValues) : ajaxValidateBorrowerResidence(formValues),
  },
  {
    id        : OVERDRAFT_PROTECTION_APP_INCOME_SOURCE,
    title     : 'What is your income source?',
    questions : overdraftProtectionQuestions.getByName([FieldNames.employmentType]),
  },
  {
    id         : OVERDRAFT_PROTECTION_APP_EMPLOYED,
    showIf     : [isEmployee, isSelfEmployed],
    title      : 'Tell us about your income and employment',
    questions  : overdraftProtectionQuestions.getByName([
      FieldNames.employCompanyName,
      FieldNames.employTitle,
      FieldNames.employStart,
      FieldNames.employBase,
      FieldNames.employPrevName,
      FieldNames.employPrevStart,
      FieldNames.employPrevEnd,
      FieldNames.incomeOnDeclineYN,
    ]),
    conditionalIf : {
      referToLoIf : [
        LoanOfficerCondition.isBorrowerShortJobDuration,
        LoanOfficerCondition.isBorrowerIncomeOnDecline,
      ],
    },
  },
  {
    id        : OVERDRAFT_PROTECTION_APP_RETIRED,
    showIf    : [isRetired],
    questions : overdraftProtectionQuestions.getByName([
      FieldNames.retirementIncome,
      FieldNames.incomeOnDeclineYN,
    ]),
    conditionalIf : {
      referToLoIf : [
        LoanOfficerCondition.isBorrowerIncomeOnDecline,
      ],
    },
  },
  {
    id        : OVERDRAFT_PROTECTION_APP_EMPLOYEMENT_OTHER,
    showIf    : [isEmploymentOther],
    questions : overdraftProtectionQuestions.getByName([
      FieldNames.otherIncome,
      FieldNames.incomeOnDeclineYN,
    ]),
    conditionalIf : {
      referToLoIf : [
        LoanOfficerCondition.isBorrowerIncomeOnDecline,
      ],
    },
  },
  {
    id        : OVERDRAFT_PROTECTION_APP_CO_BORROWER_INCOME_SOURCE,
    showIf    : [hasCoBorrower],
    title     : `What is your co-borrower's income source?`,
    questions : overdraftProtectionQuestions.getByName([
      FieldNames.coBorrowerEmploymentType,
    ]),
  },
  {
    id        : OVERDRAFT_PROTECTION_APP_CO_BORROWER_EMPLOYED,
    showIf    : [isCoEmployee, isCoSelfEmployed],
    title     : `Tell us about your co-borrower's income and employment`,
    questions : overdraftProtectionQuestions.getByName([
      FieldNames.coBorrowerEmployCompanyName,
      FieldNames.coBorrowerEmployTitle,
      FieldNames.coBorrowerEmployStart,
      FieldNames.coBorrowerEmployBase,
      FieldNames.coBorrowerEmployPrevName,
      FieldNames.coBorrowerEmployPrevStart,
      FieldNames.coBorrowerEmployPrevEnd,
      FieldNames.coBorrowerIncomeOnDeclineYN,
    ]),
    conditionalIf : {
      referToLoIf : [
        LoanOfficerCondition.isCoBorrowerIncomeOnDecline,
      ],
    },
  },
  {
    id        : OVERDRAFT_PROTECTION_APP_CO_BORROWER_RETIRED,
    showIf    : [isCoBorrowerRetired],
    questions : overdraftProtectionQuestions.getByName([
      FieldNames.coBorrowerRetirementIncome,
      FieldNames.coBorrowerIncomeOnDeclineYN,
    ]),
    conditionalIf : {
      referToLoIf : [
        LoanOfficerCondition.isCoBorrowerIncomeOnDecline,
      ],
    },
  },
  {
    id        : OVERDRAFT_PROTECTION_APP_CO_BORROWER_EMPLOYEMENT_OTHER,
    showIf    : [isCoBorrowerEmploymentOther],
    questions : overdraftProtectionQuestions.getByName([
      FieldNames.coBorrowerOtherIncome,
      FieldNames.coBorrowerIncomeOnDeclineYN,
    ]),
    conditionalIf : {
      referToLoIf : [
        LoanOfficerCondition.isCoBorrowerIncomeOnDecline,
      ],
    },
  },
  {
    id        : OVERDRAFT_PROTECTION_APP_CREDIT_AUTHORIZATION,
    title     : 'A question and your authorization',
    agreement : <AuthorizeCreditReport />,
    questions : overdraftProtectionQuestions.getByName([
      `${FieldNames.decBorrower}.${FieldNames.hasCitizenship}`,
      `${FieldNames.decCoBorrower}.${FieldNames.hasCitizenship}`,
    ]),
    conditionalIf : {
      referToLoIf : [
        LoanOfficerCondition.isBorrowerNotCitizen,
        LoanOfficerCondition.isCoBorrowerNotCitizen,
      ],
    },
    submitIf    : [(formValues) =>
      !noCitizenship(FieldNames.decBorrower)(formValues) &&
      !noCitizenship(FieldNames.decCoBorrower)(formValues),
    ],
    submitAction : ajaxPostCreditReportForOverdraftProtection,
    submitText: 'Submit',
  },
  {
    id                : OVERDRAFT_PROTECTION_APP_CONGRATS,
    title             : 'Congratulations! You have been pre-qualified!',
    questions         : [overdraftProtectionQuestions.getById(OVERDRAFT_PROTECTION_CREDIT_REPORT_RESULT_QUESTION)],
    hideBackButton    : true,
    hideForwardButton : true,
    hideProgressBar   : true,
  },
  {
    id                : OVERDRAFT_PROTECTION_APP_FINAL_SECTION,
    questions         : [overdraftProtectionQuestions.getById(OVERDRAFT_PROTECTION_FINAL_QUESTION)],
    hideBackButton    : true,
    hideForwardButton : true,
    hideProgressBar   : true,
  },
];
